/**
 * ChatbotPanel Component
 * Main panel orchestrating all chatbot sub-components
 * Handles SSE streaming, API calls, and state management
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { authFetch, authPost } from '../../utils/api';
import { readApiError } from '../../utils/api-error';
import ChatHeader from './ChatHeader';
import ContextIndicator from './ContextIndicator';
import MessageList from './MessageList';
import QuickActions from './QuickActions';
import ChatInput from './ChatInput';
import './Chatbot.css';

/**
 * Generate unique message ID
 */
function generateId() {
  return `msg_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

function stripMarkers(text) {
  return String(text || '').replace(/\[\[RF_(UNRESOLVED|HUMAN|URGENT)\]\]/g, '').trim();
}

function handoffCopy(reason) {
  if (reason === 'human') return 'You can raise a support ticket and a Rapid Fusion person will take it from here.';
  if (reason === 'urgent') return 'If the machine is stopped or this is a safety concern, raise a support ticket now.';
  return "Bob couldn't solve this. Raise a support ticket?";
}

function ChatbotPanel({ 
  isOpen, 
  isFullscreen, 
  onClose, 
  onToggleFullscreen 
}) {
  // Message state
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [pendingImage, setPendingImage] = useState(null);
  
  // Streaming state
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingMessage, setStreamingMessage] = useState('');
  const [currentModel, setCurrentModel] = useState(null);
  const [isComplexQuery, setIsComplexQuery] = useState(false);
  const [toolsInUse, setToolsInUse] = useState([]);
  
  // Error state
  const [error, setError] = useState(null);
  const [handoff, setHandoff] = useState(null);
  const navigate = useNavigate();

  // Load message history on mount
  useEffect(() => {
    if (isOpen) {
      loadHistory();
    }
  }, [isOpen]);

  /**
   * Load conversation history from server
   */
  const loadHistory = async () => {
    try {
      const response = await authFetch('/api/assistant/messages');
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.messages) {
          // Transform messages to include id if not present
          const formattedMessages = data.messages.map((msg, index) => ({
            id: msg.id || generateId(),
            role: msg.role,
            content: msg.content,
            timestamp: msg.timestamp || new Date().toISOString()
          }));
          setMessages(formattedMessages.map((msg) => ({ ...msg, content: stripMarkers(msg.content) })));
          if (data.handoff) setHandoff(data.handoff);
        }
      }
    } catch (err) {
      console.error('Failed to load chat history:', err);
    }
  };

  /**
   * Send message with streaming
   */
  const sendMessage = useCallback(async () => {
    const message = inputValue.trim();
    const image = pendingImage;

    if (!message && !image) return;

    // Clear input and error
    setInputValue('');
    setPendingImage(null);
    setError(null);

    // Add user message to UI
    const userMessage = {
      id: generateId(),
      role: 'user',
      content: message + (image ? ' [Image attached]' : ''),
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, userMessage]);

    // Prepare request body
    const requestBody = {
      message,
      stream: true
    };

    // Add image if present
    if (image) {
      requestBody.images = [{
        base64: image.base64,
        mimetype: image.mimetype
      }];
    }

    // Start streaming
    setIsStreaming(true);
    setStreamingMessage('');
    setCurrentModel(null);
    setToolsInUse([]);

    try {
      const response = await authFetch('/api/assistant/message', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(await readApiError(response));
      }

      // Check content type - handle both JSON and SSE responses
      const contentType = response.headers.get('content-type') || '';
      
      if (contentType.includes('application/json')) {
        // Server returned JSON (non-streaming response)
        const data = await response.json();
        
        if (data.success === false) {
          throw new Error(data.error || data.details || 'Request failed');
        }
        
        // Add assistant message from JSON response
        const assistantMessage = {
          id: generateId(),
          role: 'assistant',
          content: stripMarkers(data.content),
          timestamp: new Date().toISOString(),
          messageType: data.messageType
        };
        setMessages(prev => [...prev, assistantMessage]);
        if (data.handoff) setHandoff(data.handoff);
        
      } else {
        // Process SSE stream
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let fullResponse = '';
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          
          // Process complete SSE events
          const lines = buffer.split('\n');
          buffer = lines.pop() || ''; // Keep incomplete line in buffer

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));
                
                switch (data.type) {
                  case 'metadata':
                    setCurrentModel(data.model);
                    setIsComplexQuery(data.isComplexQuery);
                    break;
                    
                  case 'content':
                    fullResponse += data.content;
                    setStreamingMessage(fullResponse);
                    break;
                    
                  case 'tool_use':
                    setToolsInUse(data.tools || []);
                    break;
                    
                  case 'complete': {
                    const finalText = stripMarkers(data.content || fullResponse);
                    const assistantMessage = {
                      id: generateId(),
                      role: 'assistant',
                      content: finalText,
                      timestamp: new Date().toISOString(),
                      messageType: data.messageType
                    };
                    setMessages(prev => [...prev, assistantMessage]);
                    setStreamingMessage('');
                    setToolsInUse([]);
                    if (data.handoff) setHandoff(data.handoff);
                    break;
                  }
                    
                  case 'error':
                    setError(data.error);
                    break;
                    
                  default:
                    // Ignore unknown message types
                    break;
                }
              } catch (parseErr) {
                // Ignore parse errors for malformed SSE
                console.warn('SSE parse error:', parseErr);
              }
            }
          }
        }
        
        // If stream ended without adding a message, add what we have
        if (fullResponse && !messages.find(m => m.content === fullResponse)) {
          const assistantMessage = {
            id: generateId(),
            role: 'assistant',
            content: stripMarkers(fullResponse),
            timestamp: new Date().toISOString()
          };
          setMessages(prev => [...prev, assistantMessage]);
        }
      }
    } catch (err) {
      console.error('Chat error:', err);
      setError(err.message || 'Failed to send message');
    } finally {
      setIsStreaming(false);
    }
  }, [inputValue, pendingImage]);

  /**
   * Handle quick action
   */
  const handleQuickAction = async (action) => {
    setError(null);
    
    try {
      const response = await authPost('/api/assistant/chatbot-quick-action', {
        action,
        context: {}
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.content) {
          // Add assistant message with quick action response
          const assistantMessage = {
            id: generateId(),
            role: 'assistant',
            content: data.content,
            timestamp: new Date().toISOString()
          };
          setMessages(prev => [...prev, assistantMessage]);
        }
      } else {
        throw new Error('Quick action failed');
      }
    } catch (err) {
      console.error('Quick action error:', err);
      setError('Failed to execute quick action');
    }
  };

  /**
   * Clear conversation
   */
  const handleClear = async () => {
    if (!window.confirm('Clear all messages? This cannot be undone.')) {
      return;
    }

    try {
      const response = await authPost('/api/assistant/clear', {});
      if (response.ok) {
        setMessages([]);
        setStreamingMessage('');
        setHandoff(null);
        setError(null);
      }
    } catch (err) {
      console.error('Clear error:', err);
      setError('Failed to clear conversation');
    }
  };

  /**
   * Handle image selection
   */
  const handleImageSelect = (image) => {
    setPendingImage(image);
  };

  /**
   * Handle image removal
   */
  const handleImageRemove = () => {
    setPendingImage(null);
  };

  const raiseTicket = () => {
    const lastUser = [...messages].reverse().find((msg) => msg.role === 'user');
    const transcript = messages.slice(-20).map((msg) => `${msg.role}: ${msg.content}`).join('\n\n');
    sessionStorage.setItem('rf-ticket-draft', JSON.stringify({
      subject: String(lastUser?.content || 'Help from Bob').slice(0, 160),
      description: lastUser?.content || '',
      category: handoff?.reason === 'urgent' ? 'technical' : 'other',
      machineStopped: handoff?.reason === 'urgent',
      bobContext: transcript,
      pageUrl: window.location.pathname,
      source: 'bob',
    }));
    navigate('/support?new=1');
  };

  const sendFeedback = async (helpful) => {
    try {
      const response = await authPost('/api/tickets/bob-feedback', { helpful, scope: 'portal' });
      if (response.ok) {
        const data = await response.json();
        if (data.handoff) setHandoff(data.handoff);
      }
    } catch (err) {
      setError('Could not save that. Try again.');
    }
  };

  const lastMessage = messages[messages.length - 1];

  // Show quick actions when no messages
  const showQuickActions = messages.length === 0 && !isStreaming;

  return (
    <div className={`chatbot-panel ${isFullscreen ? 'fullscreen' : ''}`}>
      {/* Header */}
      <ChatHeader
        model={currentModel}
        isComplexQuery={isComplexQuery}
        isFullscreen={isFullscreen}
        onClear={handleClear}
        onToggleFullscreen={onToggleFullscreen}
        onClose={onClose}
      />

      {/* Context indicator */}
      <ContextIndicator messages={messages} />

      {/* Messages */}
      <MessageList
        messages={messages}
        isStreaming={isStreaming}
        streamingMessage={streamingMessage}
        toolsInUse={toolsInUse}
      />

      {/* Error display */}
      {error && (
        <div className="chatbot-error">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
          <span>{error}</span>
          <button 
            className="chatbot-error-retry"
            onClick={() => setError(null)}
          >
            Dismiss
          </button>
        </div>
      )}

      {!isStreaming && lastMessage && lastMessage.role === 'assistant' && (
        <div className="chatbot-feedback">
          <span>Did this help?</span>
          <button type="button" onClick={() => sendFeedback(true)}>Yes</button>
          <button type="button" onClick={() => sendFeedback(false)}>No</button>
        </div>
      )}

      {handoff?.offer && !isStreaming && messages.length > 0 && (
        <div className="chatbot-handoff">
          <p>{handoffCopy(handoff.reason)}</p>
          <button type="button" onClick={raiseTicket}>Raise a support ticket</button>
        </div>
      )}

      {/* Quick actions - shown when no messages */}
      {showQuickActions && (
        <QuickActions 
          onAction={handleQuickAction}
          disabled={isStreaming}
        />
      )}

      {/* Input area */}
      <ChatInput
        value={inputValue}
        onChange={setInputValue}
        onSend={sendMessage}
        onImageSelect={handleImageSelect}
        onImageRemove={handleImageRemove}
        pendingImage={pendingImage}
        disabled={isStreaming}
        placeholder={pendingImage ? "Describe what you'd like me to analyze..." : "Ask me anything about the PE320..."}
      />
    </div>
  );
}

export default ChatbotPanel;
